import { setupAutoFormLogging } from "@/utils/formLogging";
import { logInfo, logPageView, logPerformance } from "@/utils/logger";

export default class PageLogger extends Marko.Component {
  onMount() {
    // Initialize page logging when component mounts in browser
    this.initializePageLogging();
  }

  initializePageLogging() {
    // Get page information
    const path = window.location.pathname;
    const page = this.getPageNameFromPath(path);

    // Log page view
    logPageView(page, {
      url: window.location.href,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
    });

    // Log page load performance
    this.logPageLoadPerformance();

    // Setup error tracking
    this.setupErrorTracking();

    // Setup user engagement tracking
    this.setupEngagementTracking();

    // Setup form logging
    setupAutoFormLogging();
  }

  getPageNameFromPath(path: string): string {
    if (path === "/") return "home";
    if (path.startsWith("/playground")) return "playground";
    return path.replace(/^\//, "").replace(/\/$/, "") || "unknown";
  }

  logPageLoadPerformance() {
    // Log performance metrics when available
    if ("performance" in window) {
      window.addEventListener("load", () => {
        setTimeout(() => {
          const navigation = performance.getEntriesByType(
            "navigation",
          )[0] as PerformanceNavigationTiming;
          if (navigation) {
            logPerformance(
              "page_load_time",
              navigation.loadEventEnd - navigation.fetchStart,
            );
            logPerformance(
              "dom_content_loaded",
              navigation.domContentLoadedEventEnd - navigation.fetchStart,
            );
            logPerformance("first_paint", this.getFirstPaint());
          }
        }, 0);
      });
    }
  }

  getFirstPaint(): number {
    const paintEntries = performance.getEntriesByType("paint");
    const firstPaint = paintEntries.find(
      (entry) => entry.name === "first-contentful-paint",
    );
    return firstPaint ? firstPaint.startTime : 0;
  }

  setupErrorTracking() {
    // Track unhandled errors (in addition to RUM's automatic tracking)
    window.addEventListener("error", (event) => {
      logInfo("client_error_captured", {
        message: event.error?.message || event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        stack: event.error?.stack,
      });
    });

    // Track unhandled promise rejections
    window.addEventListener("unhandledrejection", (event) => {
      logInfo("unhandled_promise_rejection", {
        reason: event.reason,
        promise: event.promise.toString(),
      });
    });
  }

  setupEngagementTracking() {
    // Track time on page
    let pageStartTime = Date.now();
    let isPageVisible = !document.hidden;

    // Track page visibility changes
    document.addEventListener("visibilitychange", () => {
      if (document.hidden) {
        if (isPageVisible) {
          const timeOnPage = Date.now() - pageStartTime;
          logPerformance("time_on_page_visible", timeOnPage);
          isPageVisible = false;
        }
      } else {
        if (!isPageVisible) {
          pageStartTime = Date.now();
          isPageVisible = true;
        }
      }
    });

    // Track when user leaves the page
    window.addEventListener("beforeunload", () => {
      if (isPageVisible) {
        const timeOnPage = Date.now() - pageStartTime;
        logPerformance("time_on_page_total", timeOnPage);
      }
    });

    // Track scroll depth
    let maxScrollDepth = 0;
    window.addEventListener("scroll", () => {
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollDepth = Math.round(
        ((scrollTop + windowHeight) / documentHeight) * 100,
      );

      if (scrollDepth > maxScrollDepth) {
        maxScrollDepth = scrollDepth;

        // Log significant scroll milestones
        if (scrollDepth >= 25 && maxScrollDepth < 25) {
          logInfo("scroll_depth_milestone", { depth: "25%" });
        } else if (scrollDepth >= 50 && maxScrollDepth < 50) {
          logInfo("scroll_depth_milestone", { depth: "50%" });
        } else if (scrollDepth >= 75 && maxScrollDepth < 75) {
          logInfo("scroll_depth_milestone", { depth: "75%" });
        } else if (scrollDepth >= 100 && maxScrollDepth < 100) {
          logInfo("scroll_depth_milestone", { depth: "100%" });
        }
      }
    });
  }
}
